import {
  getObject,
  getLeoTimestamp,
  LeoTimestamp,
  getLeoUuid,
  LeoUUID,
  LeoRPCRequest,
  LeoRPCResponse,
  LeoRPCError,
  LeoRPCResult,
  LeoRPC
} from "@surya-digital/leo-ts-types";
import { ProjectName, ProjectNameDTO } from "../types/projectName";
/* eslint-disable import/export */
export abstract class UpdateProjectDetailsRPC implements
  LeoRPC<UpdateProjectDetailsRPC.Request, UpdateProjectDetailsRPC.Response, UpdateProjectDetailsRPC.Errors.Errors>
{
  abstract execute(
    request: UpdateProjectDetailsRPC.Request
  ): Promise<LeoRPCResult<UpdateProjectDetailsRPC.Response, UpdateProjectDetailsRPC.Errors.Errors>>;
}

export namespace UpdateProjectDetailsRPC {

  export class InvalidRequestError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidRequestError";
    }
  }

  export interface RequestDTO {
    projectName: ProjectNameDTO;
    startDate: string;
    endDate: string;
    projectId: string;
  }
  
  export class Request implements LeoRPCRequest {
    readonly isUpdateProjectDetailsRPCRequest: boolean = true;
  
    readonly projectName: ProjectName;
    readonly startDate: LeoTimestamp;
    readonly endDate: LeoTimestamp;
    readonly projectId: LeoUUID;
  
  
    constructor(
    projectName: ProjectName, 
    startDate: LeoTimestamp, 
    endDate: LeoTimestamp, 
    projectId: LeoUUID
    ) {
      this.projectName = projectName;
      this.startDate = startDate;
      this.endDate = endDate;
      this.projectId = projectId;
    }
  
    static fromDTO(dto: RequestDTO): Request {
      const projectName = ProjectName.fromDTO(getObject(dto, "projectName", InvalidRequestProjectNameError));
      const startDate = getLeoTimestamp(dto, "startDate", InvalidRequestStartDateError);
      const endDate = getLeoTimestamp(dto, "endDate", InvalidRequestEndDateError);
      const projectId = getLeoUuid(dto, "projectId", InvalidRequestProjectIdError);
      return new Request(
        projectName,
        startDate,
        endDate,
        projectId
      );
    }
  
    toDTO(): RequestDTO {
      const dto: RequestDTO = {
          projectName: this.projectName.toDTO(),
          startDate: this.startDate.toDTO(),
          endDate: this.endDate.toDTO(),
          projectId: this.projectId.toDTO()
      };
      return dto;
    }
    }
  
  Request.prototype.toString = function toString(): string {
    return `UpdateProjectDetailsRPC(projectName='${this.projectName}',startDate='${this.startDate}',endDate='${this.endDate}',projectId='${this.projectId}')`;
  };
  

  export class InvalidResponseError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidResponseError";
    }
  }

  export interface ResponseDTO {}

  export class Response implements LeoRPCResponse {
    static fromDTO(): Response {
      return new Response();
    }

    toDTO(): ResponseDTO {
      return {}
    }
  }

  Response.prototype.toString = function toString(): string {
    return `UpdateProjectDetailsRPC.Response()`;
  };

  export enum RPCError {
    InvalidProjectId = "INVALID_PROJECT_ID",
    ProjectAlreadyArchived = "PROJECT_ALREADY_ARCHIVED",
    ProjectNameNotUnique = "PROJECT_NAME_NOT_UNIQUE",
    InvalidProjectName = "INVALID_PROJECT_NAME",
    InvalidProjectDates = "INVALID_PROJECT_DATES"
  }

  export namespace Errors {
    
    export interface InvalidProjectIdDTO {
      code: string;
    }
    
    export class InvalidProjectId extends LeoRPCError<RPCError> {
      readonly isInvalidProjectId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidProjectId;
        super(code);
      }
    
    
      toDTO(): InvalidProjectIdDTO {
        const dto: InvalidProjectIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidProjectId.prototype.toString = function toString(): string {
      return `UpdateProjectDetailsRPC.INVALID_PROJECT_ID()`;
    };
    
    
    export interface ProjectAlreadyArchivedDTO {
      code: string;
    }
    
    export class ProjectAlreadyArchived extends LeoRPCError<RPCError> {
      readonly isProjectAlreadyArchived: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.ProjectAlreadyArchived;
        super(code);
      }
    
    
      toDTO(): ProjectAlreadyArchivedDTO {
        const dto: ProjectAlreadyArchivedDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    ProjectAlreadyArchived.prototype.toString = function toString(): string {
      return `UpdateProjectDetailsRPC.PROJECT_ALREADY_ARCHIVED()`;
    };
    
    
    export interface ProjectNameNotUniqueDTO {
      code: string;
    }
    
    export class ProjectNameNotUnique extends LeoRPCError<RPCError> {
      readonly isProjectNameNotUnique: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.ProjectNameNotUnique;
        super(code);
      }
    
    
      toDTO(): ProjectNameNotUniqueDTO {
        const dto: ProjectNameNotUniqueDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    ProjectNameNotUnique.prototype.toString = function toString(): string {
      return `UpdateProjectDetailsRPC.PROJECT_NAME_NOT_UNIQUE()`;
    };
    
    
    export interface InvalidProjectNameDTO {
      code: string;
    }
    
    export class InvalidProjectName extends LeoRPCError<RPCError> {
      readonly isInvalidProjectName: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidProjectName;
        super(code);
      }
    
    
      toDTO(): InvalidProjectNameDTO {
        const dto: InvalidProjectNameDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidProjectName.prototype.toString = function toString(): string {
      return `UpdateProjectDetailsRPC.INVALID_PROJECT_NAME()`;
    };
    
    
    export interface InvalidProjectDatesDTO {
      code: string;
    }
    
    export class InvalidProjectDates extends LeoRPCError<RPCError> {
      readonly isInvalidProjectDates: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidProjectDates;
        super(code);
      }
    
    
      toDTO(): InvalidProjectDatesDTO {
        const dto: InvalidProjectDatesDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidProjectDates.prototype.toString = function toString(): string {
      return `UpdateProjectDetailsRPC.INVALID_PROJECT_DATES()`;
    };
    

    export type Errors = InvalidProjectId | ProjectAlreadyArchived | ProjectNameNotUnique | InvalidProjectName | InvalidProjectDates;
    export type ErrorsDTO = InvalidProjectIdDTO | ProjectAlreadyArchivedDTO | ProjectNameNotUniqueDTO | InvalidProjectNameDTO | InvalidProjectDatesDTO;

    export const fromDTO = (dto: ErrorsDTO): Errors => {
      switch (dto.code) {
        case RPCError.InvalidProjectId:
          return new InvalidProjectId();
        case RPCError.ProjectAlreadyArchived:
          return new ProjectAlreadyArchived();
        case RPCError.ProjectNameNotUnique:
          return new ProjectNameNotUnique();
        case RPCError.InvalidProjectName:
          return new InvalidProjectName();
        case RPCError.InvalidProjectDates:
          return new InvalidProjectDates();
        default:
          throw new InvalidResponseError(`Unexpected error code: ${dto.code}`);
      }
    };
  }
}

export class InvalidRequestProjectNameError extends UpdateProjectDetailsRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestProjectNameError";
  }
}

export class InvalidRequestStartDateError extends UpdateProjectDetailsRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestStartDateError";
  }
}

export class InvalidRequestEndDateError extends UpdateProjectDetailsRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestEndDateError";
  }
}

export class InvalidRequestProjectIdError extends UpdateProjectDetailsRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestProjectIdError";
  }
}


export class InvalidResponseInvalidProjectIdError extends UpdateProjectDetailsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidProjectIdError";
  }
}

export class InvalidResponseProjectAlreadyArchivedError extends UpdateProjectDetailsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseProjectAlreadyArchivedError";
  }
}

export class InvalidResponseProjectNameNotUniqueError extends UpdateProjectDetailsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseProjectNameNotUniqueError";
  }
}

export class InvalidResponseInvalidProjectNameError extends UpdateProjectDetailsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidProjectNameError";
  }
}

export class InvalidResponseInvalidProjectDatesError extends UpdateProjectDetailsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidProjectDatesError";
  }
}

